import { isDev } from '@/utils/config.js';

// 国家代码常量
export const NATION_CODE = {
  ES: 'ES', // 西班牙（默认）
  AT: 'AT', // 奥地利
  DE: 'DE' // 德国
};

// 渠道 ID 常量
export const CHANNEL_ID = {
  ES: '100000', // 默认渠道（西班牙）
  AT_ONLINE: '100070', // 奥地利线上渠道
  AT_OFFLINE: '100080' // 奥地利线下渠道
};

// 缓存键名
const CACHE_KEY_CHANNEL = 'channelId';
const CACHE_KEY_NATION_CODE = 'nationCode';

/**
 * 从 URL 获取参数
 * @param {string} name - 参数名
 * @returns {string|null} 参数值
 */
export function getUrlParam(name) {
  // return null;
  if (typeof window === 'undefined') {
    return null;
  }

  const reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)', 'i');

  // 1. 尝试从 window.location.search 获取（传统方式）
  const rSearch = window.location.search.slice(1).match(reg);
  if (rSearch !== null) {
    return decodeURIComponent(rSearch[2]);
  }

  // 2. 从 window.location.hash 获取（支持 Vue Router hash 模式）
  // 例如：#/settleAccount?channelId=100080
  const hash = window.location.hash || '';
  const hashReg = new RegExp('[?&]' + name + '=([^&]*)', 'i');
  const rHash = hash.match(hashReg);
  if (rHash !== null) {
    return decodeURIComponent(rHash[1]);
  }

  return null;
}

/**
 * 从 sessionStorage 获取缓存的 channelId
 * @returns {string|null} channelId
 */
export function getCachedChannelId() {
  if (typeof window === 'undefined') {
    return null;
  }
  return sessionStorage.getItem(CACHE_KEY_CHANNEL);
}

/**
 * 保存 channelId 到 sessionStorage
 * @param {string} channelId - 要保存的 channelId
 */
export function saveChannelIdToCache(channelId) {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.setItem(CACHE_KEY_CHANNEL, channelId);
}

/**
 * 从 sessionStorage 获取缓存的 nationCode
 * @returns {string|null} nationCode
 */
export function getCachedNationCode() {
  if (typeof window === 'undefined') {
    return null;
  }
  return sessionStorage.getItem(CACHE_KEY_NATION_CODE);
}

/**
 * 保存 nationCode 到 sessionStorage
 * @param {string} nationCode - 要保存的 nationCode
 */
export function saveNationCodeToCache(nationCode) {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.setItem(CACHE_KEY_NATION_CODE, nationCode);
}

/**
 * 根据 channelId 获取对应的 nationCode
 * @param {string} channelId - 渠道 ID
 * @returns {string} nationCode
 */
export function getNationCodeByChannelId(channelId) {
  // 1. 优先从 URL 获取 nationCode
  const urlNationCode = getUrlParam('nationCode');
  if (urlNationCode) {
    const normalizedNationCode = urlNationCode.toUpperCase();
    saveNationCodeToCache(normalizedNationCode);
    return normalizedNationCode;
  }

  // 2. 如果是 100080 渠道，检查 URL 是否有 lang 参数
  const urlLang = getUrlParam('lang');
  if (channelId === CHANNEL_ID.AT_OFFLINE && !urlNationCode && !urlLang) {
    // 没有 nationCode 和 lang 参数时，使用默认值 AT
    saveNationCodeToCache(NATION_CODE.AT);
    return NATION_CODE.AT;
  }

  // 3. 从缓存获取
  const cachedNationCode = getCachedNationCode();
  if (cachedNationCode) {
    return cachedNationCode;
  }

  return null;
}

/**
 * 获取 channelId（优先级：URL 参数 > 缓存 > 默认值）
 * @returns {string} channelId
 */
export function getChannelId() {
  // 1. 先从 URL 获取
  const urlChannelId = getUrlParam('channelId');

  if (urlChannelId) {
    // URL 有参数时，覆盖并保存到缓存
    saveChannelIdToCache(urlChannelId);
    return urlChannelId;
  }

  // 2. 从缓存获取
  const cachedChannelId = getCachedChannelId();
  if (cachedChannelId) {
    return cachedChannelId;
  }

  // 3. 返回默认值
  return null;
}

/**
 * 获取 nationCode（根据 channelId 判断）
 * @returns {string} nationCode
 */
export function getNationCode() {
  const channelId = getChannelId();
  return getNationCodeByChannelId(channelId);
}

/**
 * 清除缓存的 channelId 和 nationCode
 */
export function clearChannelIdCache() {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.removeItem(CACHE_KEY_CHANNEL);
  sessionStorage.removeItem(CACHE_KEY_NATION_CODE);
}

export default {
  NATION_CODE,
  CHANNEL_ID,
  getUrlParam,
  getCachedChannelId,
  saveChannelIdToCache,
  getCachedNationCode,
  saveNationCodeToCache,
  getChannelId,
  getNationCode,
  getNationCodeByChannelId,
  clearChannelIdCache
};